import type {FondId} from 'src/features/erp-sutor/features/data-view-page';
import type {Labeled, ReferenceFieldValue, UUID} from 'src/typings';
import type {
    CompoundGridFieldValue, DynamicFieldSearchParam,
    ErrorGridFieldValue,
    Fieldable,
    GridFieldValue, GridFieldValueWithOriginReference,
    GridFieldValueWithTargetReference,
    OptionGridFieldValue, RawDynamicFieldSearchParam, RawRowCreationParams,
    RecordRow, RowCreationParams,
    ScalarGridValue,
    SimpleGridFieldValue, SupportedScalarValues
} from './types';
import {asUuid, isLabeled, isLabeledIdentified, isLabeledValue, isObject} from 'shared/utils/types-utils';
import {exists, isFunction, isNull, isNullOrUndefined} from 'shared/utils/custom-utils';
import {isString} from 'shared/utils/string-utils';
import {assertIsArray} from 'shared/utils/array-utils';
import {asFieldTypeId} from 'shared/utils/record-field-utils';

export function isErrorGridFieldValue(value: unknown): value is ErrorGridFieldValue {
    return isLabeled(value) && 'error' in value && exists(value.error);
}

export function isOptionGridFieldValue(value: unknown): value is OptionGridFieldValue {
    return isLabeled(value) && isLabeledIdentified(value);
}


export function isScalarGridValue(value: unknown): value is ScalarGridValue;
export function isScalarGridValue<VALUE extends SupportedScalarValues>(value: unknown, payloadTypeGuard: (payload: unknown) => payload is VALUE): value is ScalarGridValue<VALUE>;
export function isScalarGridValue<VALUE extends SupportedScalarValues = SupportedScalarValues>(value: unknown, payloadTypeGuard?: (payload: unknown) => payload is VALUE): value is ScalarGridValue<VALUE> {
    if (exists(payloadTypeGuard) && isFunction(payloadTypeGuard)) {
        return isLabeled(value) && isLabeledValue(value) && payloadTypeGuard(value.value);
    }
    return isLabeled(value) && isLabeledValue(value);
}

export function isSimpleGridFieldValue(value: unknown): value is SimpleGridFieldValue {
    return isNull(value) || isScalarGridValue(value) || isOptionGridFieldValue(value) || isErrorGridFieldValue(value);
}

export function hasTargetRecordReference<GRID_FIELD_SUBTYPE extends GridFieldValue = GridFieldValue>(value: GRID_FIELD_SUBTYPE): value is GridFieldValueWithTargetReference<GRID_FIELD_SUBTYPE> {
    return exists(value) && 'targetRecordReference' in value && exists(value.targetRecordReference);
}

export function assertHasTargetRecordReference<T extends GridFieldValue = GridFieldValue>(value: T): asserts value is GridFieldValueWithTargetReference<T> {
    if (!hasTargetRecordReference(value)) {
        throw new TypeError(`${JSON.stringify(value)} is not of type GridFieldValueWithTargetReference`);
    }
}

export function hasOriginRecordReference<GRID_FIELD_SUBTYPE extends GridFieldValue = GridFieldValue>(value: GRID_FIELD_SUBTYPE): value is GridFieldValueWithOriginReference<GRID_FIELD_SUBTYPE> {
    return exists(value) && 'originRecordReference' in value && exists(value.originRecordReference);
}

export function assertHasOriginRecordReference<T extends GridFieldValue = GridFieldValue>(value: T): asserts value is GridFieldValueWithOriginReference<T> {
    if (!hasOriginRecordReference(value)) {
        throw new TypeError(`${JSON.stringify(value)} is not of type GridFieldValueWithOriginReference`);
    }
}

export function assertIsScalarGridValue(value: Labeled): asserts value is ScalarGridValue {
    if (!isScalarGridValue(value)) {
        throw new TypeError(`${JSON.stringify(value)} is not of type ScalarGridValue`);
    }
}

export function assertSimpleGridFieldValue(value: unknown): asserts value is SimpleGridFieldValue {
    if (!isSimpleGridFieldValue(value)) {
        throw new TypeError(`${JSON.stringify(value)} is not of type SimpleGridFieldValue`);
    }
}

export function asSimpleGridFieldValue(value: unknown): SimpleGridFieldValue {
    assertSimpleGridFieldValue(value);
    return value;
}

export function isFieldable(value: unknown): value is Fieldable {
    return isObject(value) && 'fields' in value && exists(value.fields) && isObject(value.fields);
}

export function assertFieldable(value: unknown): asserts value is Fieldable {
    if (!isFieldable(value)) {
        throw new TypeError(`${JSON.stringify(value)} is not of type Fieldable`);
    }
}

export function isCompoundGridFieldValue(value: GridFieldValue): value is CompoundGridFieldValue {
    return isFieldable(value);
}

export function assertCompoundGridFieldValue(value: GridFieldValue): asserts value is CompoundGridFieldValue {
    if (!isCompoundGridFieldValue(value)) {
        throw new TypeError(`${JSON.stringify(value)} is not of type CompoundGridFieldValue`);
    }
}

export function isRecordRow(item: any): item is RecordRow {
    return 'id' in item && 'fields' in item;
}

export function asRecordRow(item: any): RecordRow {
    if (!isRecordRow(item)) {
        throw new TypeError(`${JSON.stringify(item)} is not a RecordRow`);
    }

    return item;
}

export function resolveDynamicFieldSearchParams(params: RawDynamicFieldSearchParam[], templatePlaceholderResolver: TemplatePlaceholderResolver): DynamicFieldSearchParam[] | undefined {
    if (isNullOrUndefined(params)) {
        return undefined;
    }

    assertIsArray(params);

    return params.map((param) => {
        return {
            fieldTypeId: asFieldTypeId(param.fieldTypeId),
            type: parseDynamicFieldSearchParamType(param.type),
            value: templatePlaceholderResolver.resolveValue(param.value)
        };
    });
}

export function resolveRowCreationParams(params: RawRowCreationParams, templatePlaceholderResolver: TemplatePlaceholderResolver): RowCreationParams | undefined {
    if (isNullOrUndefined(params)) {
        return undefined;
    }

    const initialValues = Object
        .entries(params.initialValues)
        .map(([fieldTypeId, setFieldValueRequest]) => {
            if (isReferenceFieldValue(setFieldValueRequest)) {
                setFieldValueRequest = {recordId: asUuid(templatePlaceholderResolver.resolveValue(setFieldValueRequest.recordId))};
            }
            return [fieldTypeId, setFieldValueRequest];
        })
    return {initialValues: Object.fromEntries(initialValues)};
}

function isReferenceFieldValue(value: unknown): value is ReferenceFieldValue {
    return isObject(value) && 'recordId' in value && exists(value.recordId);
}

function parseDynamicFieldSearchParamType(type: string): 'link' | 'origin' | 'val' {
    const acceptedTypes = ['link', 'origin', 'val'] as const;
    if (acceptedTypes.includes(type as any)) {
        return type as 'link' | 'origin' | 'val';
    }
    throw new Error(`Type: ${type} must be 'link', 'origin' or 'val'`);
}

export class TemplatePlaceholderResolver {
    constructor(private referenceRecordId: UUID, private fondId: FondId) {
    }

    public resolveValue(value: any) {
        if (isNullOrUndefined(value)) {
            return value;
        }

        if (isString(value)) {
            return value
                .replaceAll('${REFERENCE_RECORD_ID}', this.referenceRecordId)
                .replaceAll('${FOND_ID}', this.fondId.toString(10))
                .replaceAll('${ISO_DATE_TIME_NOW}', new Date().toISOString());
        }

        return value;
    }
}